import { Organisation, FormOrganisation } from '../types/Organisation';
import baseGoApi from "@/api/baseGoApi.ts";

export interface StripeCustomer {
    id: string;
    name: string;
    email: string;
    cardBrand?: string;
    cardLast4?: string;
    hasCard: boolean;
    deleted: boolean;
    dashboardUrl: string;
}

export interface OrganisationStripe {
    customerId?: string;
    status?: string;
    customer?: StripeCustomer;
    error?: string;
}

// Create a type for the API request that matches server expectations
type OrganisationApiRequest = Omit<Partial<Organisation>, 'facilities'> & {
    facilities?: { id: number }[];
    priceListId?: number | null;
};

const organisationApi = {
    /**
     * Fetch all organisations.
     * @param params Optional query parameters for filtering or sorting.
     * @returns A promise resolving to a list of organisations.
     */
    getAll: (params: Record<string, string | number> = {}) =>
        baseGoApi.get<Organisation[]>('/organisation/', { params }),

    /**
     * Fetch a single organisation by ID.
     * @param id The ID of the organisation.
     * @returns A promise resolving to the organisation details.
     */
    getById: async (id: number) => {
        const response = await baseGoApi.get(`/organisation/${id}`);
        const data = response.data;
        
        // Map the 'logo' field from API response to 'logoUrl' for frontend
        const organisation: Organisation = {
            ...data,
            logoUrl: data.logo || undefined
        };
        
        return { data: organisation };
    },

    /**
     * Fetch a single organisation by ID.
     * @returns A promise resolving to the organisation details.
     */
    get: async () => {
        const response = await baseGoApi.get(`/organisation/current`);
        const data = response.data;
        
        // Map the 'logo' field from API response to 'logoUrl' for frontend
        const organisation: Organisation = {
            ...data,
            logoUrl: data.logo || undefined
        };
        
        return { data: organisation };
    },

    /**
     * Create a new organisation.
     * @param data The organisation data.
     * @returns A promise resolving to the created organisation.
     */
    create: (data: Partial<FormOrganisation>) => {
        // Convert form data to API request format
        const apiData: OrganisationApiRequest = {
            ...data,
            facilities: data.facilities?.map(id => ({ id })),
            priceListId: data.priceList?.id ? parseInt(data.priceList.id) : null
        };
        // Remove the priceList object from the request
        delete (apiData as Record<string, unknown>).priceList;
        return baseGoApi.post<Organisation>('/organisation/', apiData);
    },

    /**
     * Update an organisation by ID.
     * @param id The ID of the organisation.
     * @param data The updated organisation data.
     * @returns A promise resolving to the updated organisation.
     */
    update: (id: number, data: Partial<FormOrganisation>) => {
        // Convert form data to API request format
        const apiData: OrganisationApiRequest = {
            ...data,
            facilities: data.facilities?.map(id => ({ id })),
            priceListId: data.priceList?.id ? parseInt(data.priceList.id) : null
        };
        // Remove the priceList object from the request
        delete (apiData as Record<string, unknown>).priceList;
        return baseGoApi.put<Organisation>(`/organisation/${id}`, apiData);
    },

    /**
     * Update the current organisation.
     * @param data The updated organisation data.
     * @returns A promise resolving to the updated organisation.
     */
    updateCurrent: (data: Partial<FormOrganisation>) => {
        // Convert form data to API request format
        const apiData: OrganisationApiRequest = {
            ...data,
            facilities: data.facilities?.map(id => ({ id })),
            priceListId: data.priceList?.id ? parseInt(data.priceList.id) : null
        };
        // Remove the priceList object from the request
        delete (apiData as Record<string, unknown>).priceList;
        return baseGoApi.put<Organisation>('/organisation/current', apiData);
    },

    /**
     * Delete an organisation by ID.
     * @param id The ID of the organisation.
     * @returns A promise confirming the deletion.
     */
    delete: (id: number) =>
        baseGoApi.post<void>(`/organisation/${id}/delete`, { action: 'DELETE' }),

    createFortnoxCustomer: async (organisationId: number) => {
        const response = await baseGoApi.post('/admin/integration/fortnox/create-customer', { organisationId });
        return response.data;
    },

    connectFortnoxCustomer: async (customerId: string) => {
        const response = await baseGoApi.post('/organisation/fortnox/customer', { customerId });
        return response.data;
    },

    disconnectFortnoxCustomer: async () => {
        return baseGoApi.delete(`/organisation/fortnox/customer`);
    },

    createHelpioCustomer: async (organisationId: number) => {
        const response = await baseGoApi.post('/admin/integration/helpio/customers/create', { organisationId });
        return response.data;
    },

    connectHelpioCustomer: async (customerId: string, organisationId: number) => {
        const response = await baseGoApi.post('/admin/integration/helpio/customers/connect', {
            customerId,
            organisationId
        });
        return response.data;
    },

    disconnectHelpioCustomer: async (organisationId: number) => {
        return baseGoApi.post('/admin/integration/helpio/customers/disconnect', { organisationId });
    },

    getStripeCustomer: async (organisationId: number): Promise<OrganisationStripe> => {
        const response = await baseGoApi.get(`/admin/integration/stripe/organisation/${organisationId}/customer`);
        return response.data;
    },

    searchStripeCustomers: async (query: string): Promise<StripeCustomer[]> => {
        const response = await baseGoApi.get('/admin/integration/stripe/customers/search', { params: { query } });
        return response.data.customers ?? [];
    },

    connectStripeCustomer: async (organisationId: number, customerId: string): Promise<OrganisationStripe> => {
        const response = await baseGoApi.post(`/admin/integration/stripe/organisation/${organisationId}/customer`, { customerId });
        return response.data;
    },

    disconnectStripeCustomer: async (organisationId: number) => {
        return baseGoApi.delete(`/admin/integration/stripe/organisation/${organisationId}/customer`);
    },

    syncHelpioCustomer: async (organisationId: number) => {
        const response = await baseGoApi.put('/admin/integration/helpio/customers/update', { organisationId });
        return response.data;
    },

    /**
     * Upload organisation logo
     * @param logoFile The logo file to upload
     * @returns A promise resolving to the upload result
     */
    uploadLogo: async (logoFile: File) => {
        const formData = new FormData();
        formData.append('file', logoFile);

        const response = await baseGoApi.post(`/organisation/logo`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    /**
     * Remove organisation logo
     * @returns A promise resolving to the removal result
     */
    removeLogo: async () => {
        const response = await baseGoApi.delete(`/organisation/logo`);
        return response.data;
    },

    /**
     * Update organisation setup status
     * @param setupData The setup status data to update
     * @returns A promise resolving to the updated organisation
     */
    updateSetup: async (setupData: { equipment?: boolean; logo?: boolean; organisationDetails?: boolean; package?: boolean; services?: boolean; players?: boolean }) => {
        // First get the current organisation to merge with existing setup
        const currentResponse = await baseGoApi.get('/organisation/current');
        const currentOrg = currentResponse.data;
        
        // Merge the new setup data with existing setup
        const mergedSetup = {
            ...currentOrg.setup,
            ...setupData
        };
        
        const response = await baseGoApi.put<Organisation>('/organisation/current', { setup: mergedSetup });
        return response.data;
    },

    /**
     * Get organisation subscription details
     * @returns A promise resolving to the organisation subscription data
     */
    getSubscription: () => 
        baseGoApi.get('/organisation/subscription'),

    /**
     * Get organisation invoices
     * @returns A promise resolving to the organisation invoices data
     */
    getInvoices: () => 
        baseGoApi.get('/organisation/invoices'),

    /**
     * Download an invoice by ID
     * @param id The ID of the invoice to download
     * @returns A promise resolving to the invoice file blob
     */
    downloadInvoice: async (id: number) => {
        // Backend endpoint: GET /go-api/invoices/download/:id (baseGoApi already prefixes /go-api)
        const response = await baseGoApi.get(`/invoice/download/${id}`, {
            responseType: 'blob',
        });
        return response.data;
    },
};

export const getCurrentOrganisation = async (): Promise<Organisation> => {
    try {
        const response = await baseGoApi.get(`/organisation/current`);
        const data = response.data;
        
        // Map the 'logo' field from API response to 'logoUrl' for frontend
        const organisation: Organisation = {
            ...data,
            logoUrl: data.logo || undefined
        };
        
        return organisation;
    } catch (error: unknown) {
        console.error(`Error fetching organisation`, error);
        throw new Error(`Failed to fetch organisation`);
    }
};

export default organisationApi;
